import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { Plus, X, Landmark, TrendingDown, AlertTriangle, Clock, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/format';

const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-amber-100 text-amber-700',
  'Manager Approved': 'bg-amber-100 text-amber-800',
  'HR Approved': 'bg-sky-100 text-sky-700',
  Approved: 'bg-indigo-100 text-indigo-700',
  Disbursed: 'bg-emerald-100 text-emerald-700',
  Recovering: 'bg-teal-100 text-teal-700',
  Closed: 'bg-slate-200 text-slate-700',
  Rejected: 'bg-rose-100 text-rose-700',
  Cancelled: 'bg-slate-100 text-slate-500',
  'Written Off': 'bg-red-100 text-red-700',
};
const STAGE_LABEL = { reporting_manager: 'Reporting Manager', hr: 'HR', finance: 'Finance' };
const FILTERS = ['active', 'Submitted', 'Approved', 'Recovering', 'history', 'all'];
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDate = (s) => formatDate(s);
const err = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.errors?.length) return d.errors.join(' · ');
  return d?.message || 'Action failed';
};

export default function SalaryAdvances() {
  const [confirm, confirmHost] = useConfirm();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([
        apiGet(`/hr/salary-advances?status=${status}&page=${page}&limit=20${q ? `&q=${encodeURIComponent(q)}` : ''}`),
        apiGet('/hr/salary-advances/summary').catch(() => null),
      ]);
      setRows(list.advances || []);
      setMeta({ total: list.total ?? list.count ?? 0, pages: list.pages || 1 });
      setSummary(sum);
    } catch (e) { toast.error(err(e)); }
  }, [status, page, q]);

  useEffect(() => { load(); }, [load]);

  const open = async (adv) => {
    try { setSelected(await apiGet(`/hr/salary-advances/${adv.id}`)); }
    catch (e) { toast.error(err(e)); }
  };

  const act = async (adv, action, extra = {}) => {
    if (action === 'write-off') {
      const ok = await confirm({
        title: `Write off ${adv.advance_no}?`, danger: true, requirePhrase: 'WRITE OFF',
        body: `The remaining ${inr(adv.outstanding_total)} will be waived and can never be recovered. This is restricted to MD / Super Admin and is fully audited.`,
        confirmLabel: 'Write off balance',
      });
      if (!ok) return;
    }
    if (action === 'disburse') {
      const ok = await confirm({
        title: `Disburse ${inr(adv.principal)} to ${adv.employee_name}?`, requirePhrase: 'DISBURSE',
        body: extra.mode === 'bank_transfer'
          ? 'A draft payment voucher will be created in Finance. Approving that voucher releases the money and posts the GL entry.'
          : `Mode: ${extra.mode}. The GL entry is posted immediately.`,
        confirmLabel: 'Disburse',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      if (action === 'submit') await apiPost(`/hr/salary-advances/${adv.id}/submit`);
      else if (action === 'approve') await apiPut(`/hr/salary-advances/${adv.id}/approve`, { remarks: extra.remarks || '' });
      else if (action === 'reject') await apiPut(`/hr/salary-advances/${adv.id}/reject`, { reason: extra.reason });
      else if (action === 'cancel') await apiPut(`/hr/salary-advances/${adv.id}/cancel`, { reason: extra.reason });
      else if (action === 'disburse') await apiPost(`/hr/salary-advances/${adv.id}/disburse`, extra);
      else if (action === 'prepay') await apiPost(`/hr/salary-advances/${adv.id}/prepay`, extra);
      else if (action === 'pause') await apiPost(`/hr/salary-advances/${adv.id}/pause`, extra);
      else if (action === 'write-off') await apiPost(`/hr/salary-advances/${adv.id}/write-off`, { reason: extra.reason });
      toast.success('Done');
      await load();
      await open(adv);
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="salary-advances-page">
      {confirmHost}
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Payroll</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Salary Advances</h1>
          <p className="text-sm text-slate-600 mt-1">Interest-free (or flat-simple) advances recovered as EMIs from net salary — manager → HR → Finance.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-advance-btn">
          <Plus size={16} /> Raise for Employee
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="advance-kpis">
          <Kpi icon={Landmark} label="Outstanding" value={inr(summary.total_outstanding)} sub={`${summary.open_advances} open advance(s)`} testid="kpi-outstanding" />
          <Kpi icon={TrendingDown} label={`Recovered · ${summary.month}`} value={inr(summary.recovered_this_month)} sub={`${summary.partial_recoveries_this_month} partial`} testid="kpi-recovered" />
          <Kpi icon={AlertTriangle} label="Shortfalls this month" value={summary.shortfalls_this_month} sub="carried to next month" tone={summary.shortfalls_this_month ? 'warn' : ''} testid="kpi-shortfalls" />
          <Kpi icon={Clock} label="Pending approval" value={summary.pending_approval} sub="awaiting a decision" testid="kpi-pending" />
          <Kpi icon={Wallet} label="Awaiting disbursement" value={summary.awaiting_disbursement} sub="approved, not released" testid="kpi-awaiting" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map(s => (
          <button key={s} onClick={() => { setStatus(s); setPage(1); }} data-testid={`filter-${s}`}
            className={`px-3 py-1 text-xs rounded-sm border capitalize ${status === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600 hover:border-[#3b4872]'}`}>
            {s === 'history' ? 'History (closed · rejected · written off)' : s}
          </button>
        ))}
        <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search employee / advance no…"
          className="input ml-auto max-w-[260px]" data-testid="advance-search" />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr><th>Advance #</th><th>Employee</th><th>Principal</th><th>EMI</th><th>Progress</th><th>Outstanding</th><th>Next EMI</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map(a => (
              <tr key={a.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => open(a)} data-testid={`advance-row-${a.advance_no}`}>
                <td className="font-mono text-xs">{a.advance_no}</td>
                <td><div className="font-display font-semibold">{a.employee_name}</div><div className="text-xs text-slate-500">{a.emp_code} · {a.department || '—'}</div></td>
                <td className="font-display font-bold">{inr(a.principal)}</td>
                <td className="text-xs">{inr(a.emi_amount)} × {a.tenure_months}m{a.interest_type === 'flat_simple' ? ` @ ${a.annual_rate}%` : ''}</td>
                <td className="text-xs">{a.emis_paid || 0}/{a.emis_total || a.tenure_months}</td>
                <td className="font-display font-bold text-[#3b4872]">{inr(a.outstanding_total)}</td>
                <td className="text-xs font-mono">{a.next_emi_month || '—'}</td>
                <td>
                  <span className={`tag ${STATUS_COLOR[a.status] || ''}`}>{a.status}</span>
                  {a.pending_stage && <div className="text-[10px] text-slate-500 mt-0.5">with {STAGE_LABEL[a.pending_stage]}</div>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="8" className="p-8 text-center text-sm text-slate-500" data-testid="advances-empty">No salary advances for this filter.</td></tr>}
          </tbody>
        </table>
        {meta.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs">
            <span className="text-slate-500">{meta.total} advance(s)</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-secondary py-1 px-3 disabled:opacity-40" data-testid="page-prev">Prev</button>
              <span className="px-2 py-1">Page {page} / {meta.pages}</span>
              <button disabled={page >= meta.pages} onClick={() => setPage(p => p + 1)} className="btn-secondary py-1 px-3 disabled:opacity-40" data-testid="page-next">Next</button>
            </div>
          </div>
        )}
      </div>

      {showForm && <RaiseForm onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
      {selected && (
        <AdvanceDrawer
          adv={selected} busy={busy}
          onClose={() => setSelected(null)}
          onAct={act}
        />
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone, testid }) {
  return (
    <div className={`bg-white border rounded-sm p-4 ${tone === 'warn' ? 'border-amber-300' : 'border-slate-200'}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">
        <Icon size={12} /> {label}
      </div>
      <div className="font-display text-2xl font-extrabold text-[#0F172A] mt-1">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

// ── Raise on behalf of an employee ───────────────────────────────────────────
function RaiseForm({ onClose, onDone }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: '', principal: '', tenure_months: 6, interest_type: 'none', annual_rate: 0, start_month: '', purpose: '' });
  const [elig, setElig] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiGet('/hr/employees').then(r => setEmployees(Array.isArray(r) ? r : (r.employees || []))).catch(() => {}); }, []);

  // Earliest permitted first-EMI month = the next un-run payroll month (server-derived)
  useEffect(() => {
    apiPost('/hr/salary-advances/preview', { principal: 1, tenure_months: 1 })
      .then(p => setForm(f => (f.start_month ? f : { ...f, start_month: p.schedule?.[0]?.month || '' })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.employee_id) return;
    const emp = employees.find(e => e.id === form.employee_id);
    if (!emp) return;
    setElig({ loading: true });
    apiGet(`/hr/salary-advances?employee_id=${form.employee_id}&status=active`)
      .then(r => setElig({
        active: r.advances || [],
        gross: emp.gross_monthly || ((emp.basic || 0) + (emp.hra || 0) + (emp.allowances || 0)),
        employment_status: emp.employment_status,
      }))
      .catch(() => setElig(null));
  }, [form.employee_id, employees]);

  useEffect(() => {
    const p = parseFloat(form.principal) || 0;
    const t = parseInt(form.tenure_months, 10) || 0;
    if (p <= 0 || t <= 0) { setPreview(null); return; }
    const timer = setTimeout(() => {
      apiPost('/hr/salary-advances/preview', {
        principal: p, tenure_months: t, interest_type: form.interest_type,
        annual_rate: parseFloat(form.annual_rate) || 0, start_month: form.start_month || undefined,
      }).then(setPreview).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(timer);
  }, [form.principal, form.tenure_months, form.interest_type, form.annual_rate, form.start_month]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiPost('/hr/salary-advances?submit=true', {
        ...form, principal: parseFloat(form.principal), tenure_months: parseInt(form.tenure_months, 10),
        annual_rate: parseFloat(form.annual_rate) || 0,
      });
      toast.success('Advance raised and sent for approval');
      onDone();
    } catch (e2) { toast.error(err(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-xl w-full max-h-[92vh] overflow-y-auto">
        <div className="border-b border-slate-200 p-5 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Raise Salary Advance</h2>
          <button onClick={onClose} className="text-slate-500" data-testid="close-raise-form"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="label">Employee *</label>
            <select value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} className="input" required data-testid="advance-employee-select">
              <option value="">Select</option>
              {employees.map(e => <option key={e.id} value={e.id}>{`${e.emp_id} — ${e.name}`}</option>)}
            </select>
          </div>
          {elig && !elig.loading && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs space-y-1" data-testid="advance-eligibility">
              <div className="flex justify-between"><span className="text-slate-500">Monthly gross</span><span className="font-mono">{inr(elig.gross)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Employment status</span><span>{elig.employment_status || '—'}</span></div>
              {elig.active?.length > 0 && (
                <div className="text-amber-700 font-semibold pt-1">
                  Already has an active advance ({elig.active[0].advance_no} · {elig.active[0].status}) — policy allows one at a time.
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Principal (₹) *</label><input type="number" min="1" value={form.principal} onChange={e => setForm({ ...form, principal: e.target.value })} className="input" required data-testid="advance-principal-input" /></div>
            <div><label className="label">Tenure (months) *</label><input type="number" min="1" max="24" value={form.tenure_months} onChange={e => setForm({ ...form, tenure_months: e.target.value })} className="input" required data-testid="advance-tenure-input" /></div>
            <div>
              <label className="label">Interest</label>
              <select value={form.interest_type} onChange={e => setForm({ ...form, interest_type: e.target.value })} className="input" data-testid="advance-interest-select">
                <option value="none">Interest-free</option>
                <option value="flat_simple">Flat simple</option>
              </select>
            </div>
            {form.interest_type === 'flat_simple'
              ? <div><label className="label">Rate (% p.a.) *</label><input type="number" min="0.1" step="0.1" value={form.annual_rate} onChange={e => setForm({ ...form, annual_rate: e.target.value })} className="input" required /></div>
              : <div><label className="label">First EMI month</label><input type="month" value={form.start_month} onChange={e => setForm({ ...form, start_month: e.target.value })} className="input" data-testid="advance-start-month" /></div>}
          </div>
          {form.interest_type === 'flat_simple' && (
            <div><label className="label">First EMI month</label><input type="month" value={form.start_month} onChange={e => setForm({ ...form, start_month: e.target.value })} className="input" /></div>
          )}
          <div><label className="label">Purpose *</label><textarea value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} className="input" rows={2} required minLength={3} data-testid="advance-purpose-input" /></div>

          {preview && (
            <div className="bg-indigo-50/50 border border-indigo-200 rounded-sm p-3" data-testid="advance-emi-preview">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#3b4872] mb-2">EMI preview</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-600">EMI</span><span className="font-mono font-bold">{inr(preview.emi_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">EMIs</span><span className="font-mono">{preview.emis_total}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Interest</span><span className="font-mono">{inr(preview.total_interest)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Repayable</span><span className="font-mono font-bold">{inr(preview.total_repayable)}</span></div>
                <div className="col-span-full text-[10px] text-slate-500">Recovery {preview.schedule?.[0]?.month} → {preview.end_month}, deducted from net salary after statutory deductions.</div>
              </div>
            </div>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="submit-advance-btn">{saving ? 'Submitting…' : 'Raise & Send for Approval'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detail drawer ────────────────────────────────────────────────────────────
function AdvanceDrawer({ adv, busy, onClose, onAct }) {
  const [tab, setTab] = useState('schedule');
  const [reason, setReason] = useState('');
  const [prepay, setPrepay] = useState({ amount: '', mode: 'reduce_tenure' });
  const [disburse, setDisburse] = useState({ mode: 'bank_transfer', payment_ref: '' });
  const canApprove = ['Submitted', 'Manager Approved', 'HR Approved'].includes(adv.status);
  const canCancel = ['Draft', 'Submitted', 'Manager Approved', 'HR Approved', 'Approved'].includes(adv.status);
  const recovering = ['Disbursed', 'Recovering'].includes(adv.status);
  const paid = useMemo(() => (adv.schedule || []).filter(r => r.status === 'Paid').length, [adv.schedule]);

  return (
    <div className="fixed inset-0 z-50 flex">
      <button className="flex-1 bg-black/40" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-[620px] bg-slate-50 border-l border-slate-200 flex flex-col" data-testid="advance-drawer">
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">{adv.advance_no}</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight">{inr(adv.principal)}</h2>
            <div className="text-sm text-slate-600 mt-0.5">{adv.employee_name} · {adv.emp_code} · {adv.department || '—'}</div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`tag ${STATUS_COLOR[adv.status] || ''}`} data-testid="drawer-status">{adv.status}</span>
              {adv.pending_stage && <span className="text-xs text-slate-500">with {STAGE_LABEL[adv.pending_stage]}</span>}
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-sm" data-testid="close-drawer"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-10">
            <Row label="EMI" value={`${inr(adv.emi_amount)} × ${adv.tenure_months}m`} />
            <Row label="Interest" value={adv.interest_type === 'none' ? 'Interest-free' : `Flat simple @ ${adv.annual_rate}%`} />
            <Row label="Total repayable" value={inr(adv.total_repayable)} />
            <Row label="Recovered" value={`${inr(adv.recovered_total)} (${paid}/${adv.emis_total || adv.tenure_months})`} />
            <Row label="Outstanding" value={inr(adv.outstanding_total)} accent />
            <Row label="Next EMI" value={adv.next_emi_month || '—'} />
            <Row label="Recovery window" value={`${adv.start_month} → ${adv.end_month}`} />
            <Row label="Disbursed" value={adv.disbursed_at ? `${fmtDate(adv.disbursed_at)} · ${adv.disbursement_mode}` : '—'} />
            {adv.payment_voucher_no && <Row label="Payment voucher" value={`${adv.payment_voucher_no} (${adv.payment_voucher_status || 'draft'})`} />}
            {adv.fnf_shortfall > 0 && <Row label="F&F shortfall" value={inr(adv.fnf_shortfall)} accent />}
            <div className="sm:col-span-2 border-t border-slate-100 pt-2 mt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Purpose</div>
              <div className="text-xs">{adv.purpose}</div>
            </div>
            {adv.reject_reason && <div className="sm:col-span-2 text-xs text-rose-700">Rejected: {adv.reject_reason}</div>}
            {adv.write_off_reason && <div className="sm:col-span-2 text-xs text-red-700">Written off: {adv.write_off_reason}</div>}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="flex border-b border-slate-200">
              {['schedule', 'ledger', 'approvals'].map(t => (
                <button key={t} onClick={() => setTab(t)} data-testid={`drawer-tab-${t}`}
                  className={`px-4 py-2.5 text-xs font-display font-bold uppercase tracking-wider capitalize ${tab === t ? 'text-[#3b4872] border-b-2 border-[#3b4872]' : 'text-slate-500'}`}>{t}</button>
              ))}
            </div>
            {tab === 'schedule' && (
              <table className="w-full dense-table" data-testid="drawer-schedule">
                <thead><tr><th>Month</th><th>EMI</th><th>Principal</th><th>Interest</th><th>Status</th></tr></thead>
                <tbody>
                  {(adv.schedule || []).map((r, i) => (
                    <tr key={`${r.month}-${i}`}>
                      <td className="font-mono text-xs">{r.month}{r.origin_month ? ' *' : ''}</td>
                      <td className="font-display font-semibold">{inr(r.emi)}</td>
                      <td className="text-xs">{inr(r.principal_part)}</td>
                      <td className="text-xs">{inr(r.interest_part)}</td>
                      <td className="text-xs">{r.status}{r.status === 'Partial' ? ` (${inr(r.paid_amount)})` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === 'ledger' && (
              <table className="w-full dense-table" data-testid="drawer-ledger">
                <thead><tr><th>Date</th><th>Entry</th><th>Amount</th><th>Balance</th><th>Remarks</th></tr></thead>
                <tbody>
                  {(adv.ledger || []).map(e => (
                    <tr key={e.id}>
                      <td className="text-xs">{fmtDate(e.created_at)}</td>
                      <td className="text-xs capitalize">{(e.entry_type || '').replace(/_/g, ' ')}{e.reversed ? ' (reversed)' : ''}</td>
                      <td className="font-display font-semibold">{inr(e.amount)}</td>
                      <td className="text-xs">{inr(e.balance_after)}</td>
                      <td className="text-[11px] text-slate-500">{e.remarks || '—'}{e.payroll_month ? ` · ${e.payroll_month}` : ''}</td>
                    </tr>
                  ))}
                  {!(adv.ledger || []).length && <tr><td colSpan="5" className="p-5 text-center text-xs text-slate-500">Nothing recovered or disbursed yet.</td></tr>}
                </tbody>
              </table>
            )}
            {tab === 'approvals' && (
              <ul className="divide-y divide-slate-100" data-testid="drawer-approvals">
                {(adv.approvals || []).map((a, i) => (
                  <li key={i} className="px-4 py-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-display font-semibold">{STAGE_LABEL[a.stage] || a.stage}</span>
                      <span className={a.decision === 'Rejected' ? 'text-rose-700 text-xs' : 'text-emerald-700 text-xs'}>{a.decision || 'Approved'}</span>
                    </div>
                    <div className="text-xs text-slate-500">{a.by} · {fmtDate(a.at)}{a.remarks ? ` · ${a.remarks}` : ''}</div>
                  </li>
                ))}
                {!(adv.approvals || []).length && <li className="p-5 text-center text-xs text-slate-500">No approvals recorded yet.</li>}
              </ul>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
            <div className="text-[11px] font-display font-bold uppercase tracking-wider text-slate-500">Actions</div>
            {(adv.status === 'Draft' || adv.status === 'Rejected') && (
              <button disabled={busy} onClick={() => onAct(adv, 'submit')} className="btn-primary w-full" data-testid="action-submit">Send for approval</button>
            )}
            {canApprove && (
              <div className="flex gap-2">
                <button disabled={busy} onClick={() => onAct(adv, 'approve')} className="btn-primary flex-1" data-testid="action-approve">Approve ({STAGE_LABEL[adv.pending_stage]})</button>
                <button disabled={busy || !reason.trim()} onClick={() => onAct(adv, 'reject', { reason })} className="btn-secondary flex-1 text-rose-700 disabled:opacity-40" data-testid="action-reject">Reject</button>
              </div>
            )}
            {adv.status === 'Approved' && (
              <div className="space-y-2 border border-emerald-200 bg-emerald-50/40 rounded-sm p-3">
                <div className="text-xs font-semibold text-emerald-800">Disburse (Finance)</div>
                <div className="grid grid-cols-2 gap-2">
                  <select value={disburse.mode} onChange={e => setDisburse({ ...disburse, mode: e.target.value })} className="input" data-testid="disburse-mode">
                    <option value="bank_transfer">Bank transfer (creates payment voucher)</option>
                    <option value="with_salary">With next salary</option>
                    <option value="cash">Cash</option>
                  </select>
                  <input value={disburse.payment_ref} onChange={e => setDisburse({ ...disburse, payment_ref: e.target.value })} placeholder="Reference (optional)" className="input" />
                </div>
                <button disabled={busy} onClick={() => onAct(adv, 'disburse', disburse)} className="btn-primary w-full" data-testid="action-disburse">Disburse {inr(adv.principal)}</button>
              </div>
            )}
            {recovering && (
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-slate-200 rounded-sm p-3 space-y-2">
                  <div className="text-xs font-semibold">Prepay</div>
                  <input type="number" min="1" value={prepay.amount} onChange={e => setPrepay({ ...prepay, amount: e.target.value })} placeholder="Amount" className="input" data-testid="prepay-amount" />
                  <select value={prepay.mode} onChange={e => setPrepay({ ...prepay, mode: e.target.value })} className="input">
                    <option value="reduce_tenure">Reduce tenure</option>
                    <option value="reduce_emi">Reduce EMI</option>
                  </select>
                  <button disabled={busy || !prepay.amount} onClick={() => onAct(adv, 'prepay', { amount: parseFloat(prepay.amount), mode: prepay.mode })} className="btn-secondary w-full disabled:opacity-40" data-testid="action-prepay">Apply prepayment</button>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 space-y-2">
                  <div className="text-xs font-semibold">EMI holiday / write-off</div>
                  <button disabled={busy || !reason.trim()} onClick={() => onAct(adv, 'pause', { months: 1, reason })} className="btn-secondary w-full disabled:opacity-40" data-testid="action-pause">Pause 1 month</button>
                  <button disabled={busy || !reason.trim()} onClick={() => onAct(adv, 'write-off', { reason })} className="btn-secondary w-full text-red-700 disabled:opacity-40" data-testid="action-write-off">Write off balance</button>
                </div>
              </div>
            )}
            {canCancel && (
              <button disabled={busy || !reason.trim()} onClick={() => onAct(adv, 'cancel', { reason })} className="btn-secondary w-full disabled:opacity-40" data-testid="action-cancel">Cancel advance</button>
            )}
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Reason — required for reject, cancel, pause and write-off"
              className="input" data-testid="advance-reason-input" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className={`text-right ${accent ? 'font-display font-bold text-[#3b4872]' : 'font-display font-semibold'}`}>{value}</span>
    </div>
  );
}
